import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch as AntSwitch,
  Tag,
  Typography,
  message,
} from 'antd';
import { BranchConfigApi } from 'apis/BranchConfigApis';
import { Button, ButtonFilter, FormFilter, InputForm, Table } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HocChangePagination } from 'utils/HocChangePagination';

const { Text } = Typography;

interface IBranchConfigItem {
  id?: number | string;
  plantId?: string;
  plantName?: string;
  terminalId?: string;
  merchantId?: string;
  prctr?: string;
  description?: string;
  active?: boolean;
}

interface IBranchConfigResponse {
  code?: number;
  data?: {
    items?: IBranchConfigItem[];
    total?: number;
  };
  message?: string;
}

export const BranchConfigPage = () => {
  const { setTitle, setBreadcrumb } = useTitle();
  const navigate = useNavigate();
  const queryParam = useQueryParam();

  const page = parseInt(queryParam.get('page') + '') || 1;
  const pageSize = parseInt(queryParam.get('page_size') + '') || 10;

  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataList, setDataList] = useState<IBranchConfigItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<IBranchConfigItem | null>(
    null,
  );
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [filterForm] = Form.useForm();
  const [modalForm] = Form.useForm();

  useEffect(() => {
    setTitle('Thông số chi nhánh');
    setBreadcrumb([{ title: 'Hệ thống' }, { title: 'Thông số chi nhánh' }]);
  }, [setTitle, setBreadcrumb]);

  const fetchData = useCallback(
    async (p = 1, pSize = 10, filters: Record<string, string> = {}) => {
      setLoading(true);
      try {
        const searchTerms = [
          filters.plantId,
          filters.plantName,
          filters.terminalId,
          filters.merchantId,
        ]
          .filter(Boolean)
          .join(' ');

        const rawRes = await BranchConfigApi.getBranchConfigs({
          page: p,
          limit: pSize,
          search: searchTerms,
        });

        const res = rawRes as unknown as IBranchConfigResponse;
        if (res?.code === 0) {
          setDataList(res.data?.items || []);
          setTotalItems(res.data?.total || 0);
        } else {
          message.error(
            res?.message || 'Không thể tải danh sách cấu hình chi nhánh',
          );
        }
      } catch (err: unknown) {
        const errorObj = err as { message?: string };
        message.error(errorObj?.message || 'Có lỗi xảy ra khi kết nối server');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(page, pageSize, filterParams);
  }, [fetchData, page, pageSize, filterParams]);

  const handleFilterFinish = (values: Record<string, string>) => {
    setFilterParams(values);
    navigate(`?page=1&page_size=${pageSize}`);
  };

  const handleResetFilter = () => {
    filterForm.resetFields();
    setFilterParams({});
    navigate(`?page=1&page_size=${pageSize}`);
  };

  const handleOpenAddModal = () => {
    setEditingItem(null);
    modalForm.resetFields();
    modalForm.setFieldsValue({
      active: true,
      terminalId: '',
      merchantId: '',
      prctr: '',
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = useCallback(
    (record: IBranchConfigItem) => {
      setEditingItem(record);
      modalForm.setFieldsValue({
        plantId: record.plantId,
        plantName: record.plantName,
        terminalId: record.terminalId,
        merchantId: record.merchantId,
        prctr: record.prctr,
        description: record.description,
        active: record.active !== false,
      });
      setIsModalOpen(true);
    },
    [modalForm],
  );

  const handleModalSubmit = async () => {
    try {
      const values = await modalForm.validateFields();
      setSubmitting(true);

      if (editingItem) {
        // Update
        const rawRes = await BranchConfigApi.updateBranchConfig(
          Number(editingItem.id),
          {
            plantName: values.plantName,
            terminalId: values.terminalId,
            merchantId: values.merchantId,
            prctr: values.prctr,
            description: values.description,
            active: values.active,
          },
        );
        const res = rawRes as unknown as IBranchConfigResponse;
        if (res?.code === 0) {
          message.success('Cập nhật thông số chi nhánh thành công!');
          setIsModalOpen(false);
          fetchData(page, pageSize, filterParams);
        } else {
          message.error(res?.message || 'Không thể cập nhật cấu hình');
        }
      } else {
        // Create
        const rawRes = await BranchConfigApi.createBranchConfig({
          plantId: values.plantId,
          plantName: values.plantName,
          terminalId: values.terminalId,
          merchantId: values.merchantId,
          prctr: values.prctr,
          description: values.description,
          active: values.active,
        });
        const res = rawRes as unknown as IBranchConfigResponse;
        if (res?.code === 0) {
          message.success('Thêm thông số chi nhánh thành công!');
          setIsModalOpen(false);
          fetchData(page, pageSize, filterParams);
        } else {
          message.error(res?.message || 'Không thể thêm mới cấu hình');
        }
      }
    } catch (err: unknown) {
      const errorObj = err as { errorFields?: unknown; message?: string };
      if (errorObj?.errorFields) return;
      message.error(errorObj?.message || 'Lỗi xử lý dữ liệu');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        width: 60,
        align: 'center',
        render: (_, __, index: number) => index + 1 + pageSize * (page - 1),
      },
      {
        title: 'Mã chi nhánh',
        dataIndex: 'plantId',
        key: 'plantId',
        width: 140,
        render: (val: string) => (
          <Tag color="blue" style={{ fontSize: 13, fontWeight: 700 }}>
            {val || '-'}
          </Tag>
        ),
      },
      {
        title: 'Tên chi nhánh',
        dataIndex: 'plantName',
        key: 'plantName',
        width: 220,
        render: (val: string) => <Text strong>{val || '-'}</Text>,
      },
      {
        title: 'Terminal ID',
        dataIndex: 'terminalId',
        key: 'terminalId',
        width: 150,
        render: (val: string) => (val ? <Tag color="purple">{val}</Tag> : '-'),
      },
      {
        title: 'Merchant ID',
        dataIndex: 'merchantId',
        key: 'merchantId',
        width: 160,
        render: (val: string) => (val ? <Tag color="cyan">{val}</Tag> : '-'),
      },
      {
        title: 'Profit Center (PRCTR)',
        dataIndex: 'prctr',
        key: 'prctr',
        width: 180,
        render: (val: string) =>
          val ? <Tag color="geekblue">{val}</Tag> : '-',
      },
      {
        title: 'Ghi chú',
        dataIndex: 'description',
        key: 'description',
        width: 250,
        render: (val: string) => val || '-',
      },
      {
        title: 'Trạng thái',
        dataIndex: 'active',
        key: 'active',
        width: 140,
        align: 'center',
        render: (val: boolean) => (
          <Tag color={val !== false ? 'success' : 'default'}>
            {val !== false ? 'Hoạt động' : 'Ngưng'}
          </Tag>
        ),
      },
      {
        title: 'Thao tác',
        key: 'action',
        width: 100,
        align: 'center',
        fixed: 'right',
        render: (_, record: IBranchConfigItem) => (
          <Button
            fill={false}
            title="Sửa"
            iconBefore={<EditOutlined />}
            handleOnclick={() => handleOpenEditModal(record)}
          />
        ),
      },
    ],
    [page, pageSize, handleOpenEditModal],
  );

  return (
    <>
      {/* Upper Action Bar */}
      <Row gutter={[8, 16]} align="middle" justify="end" className="mb-2">
        <Col className="flex gap-2 align-middle">
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
          <Button
            fill
            title="Thêm chi nhánh mới"
            iconBefore={<PlusOutlined />}
            handleOnclick={handleOpenAddModal}
          />
        </Col>
      </Row>

      {/* Filter Section */}
      <Content isOpen={isOpenFilter}>
        <FormFilter
          form={filterForm}
          onCancel={handleResetFilter}
          onFinish={handleFilterFinish}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Form.Item name="plantId" label="Mã chi nhánh">
                <InputForm name="plantId" placeholder="Nhập mã chi nhánh" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="plantName" label="Tên chi nhánh">
                <InputForm name="plantName" placeholder="Nhập tên chi nhánh" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="terminalId" label="Terminal ID">
                <InputForm name="terminalId" placeholder="Nhập Terminal ID" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="merchantId" label="Merchant ID">
                <InputForm name="merchantId" placeholder="Nhập Merchant ID" />
              </Form.Item>
            </Col>
          </Row>
        </FormFilter>
      </Content>

      {/* Data Table Section */}
      <Content>
        <Table
          columns={columns}
          dataSource={dataList}
          loading={loading}
          rowKey={(r: IBranchConfigItem) => String(r.id || r.plantId || '')}
          scroll={{ x: 1240 }}
          bordered
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: HocChangePagination(),
          }}
        />
      </Content>

      {/* Modal Add / Edit */}
      <Modal
        title={
          editingItem
            ? `Chỉnh sửa thông số chi nhánh ${editingItem.plantId}`
            : 'Thêm mới thông số chi nhánh'
        }
        open={isModalOpen}
        onOk={handleModalSubmit}
        confirmLoading={submitting}
        onCancel={() => setIsModalOpen(false)}
        okText="Lưu thông số"
        cancelText="Hủy"
        width={560}
      >
        <Form form={modalForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Mã chi nhánh (Plant ID)"
            name="plantId"
            rules={[{ required: true, message: 'Vui lòng nhập Mã chi nhánh' }]}
          >
            <Input placeholder="VD: 5001" disabled={!!editingItem} />
          </Form.Item>

          <Form.Item
            label="Tên chi nhánh"
            name="plantName"
            rules={[{ required: true, message: 'Vui lòng nhập Tên chi nhánh' }]}
          >
            <Input placeholder="VD: Chi nhánh Nhiêu Tâm" />
          </Form.Item>

          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item
              label="Terminal ID"
              name="terminalId"
              rules={[{ required: true, message: 'Vui lòng nhập Terminal ID' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="VD: P05901" />
            </Form.Item>

            <Form.Item
              label="Merchant ID"
              name="merchantId"
              rules={[{ required: true, message: 'Vui lòng nhập Merchant ID' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="VD: VCBQR901" />
            </Form.Item>
          </Space>

          <Form.Item
            label="Profit Center (PRCTR)"
            name="prctr"
            rules={[{ required: true, message: 'Vui lòng nhập Profit Center' }]}
          >
            <Input placeholder="VD: 0050015001" />
          </Form.Item>

          <Form.Item label="Ghi chú" name="description">
            <Input.TextArea rows={3} placeholder="Nhập ghi chú chi tiết..." />
          </Form.Item>

          <Form.Item
            label="Trạng thái hoạt động"
            name="active"
            valuePropName="checked"
          >
            <AntSwitch checkedChildren="Hoạt động" unCheckedChildren="Ngưng" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BranchConfigPage;
